import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Tooltip,
  Empty,
  Input,
  Row,
  Col,
  Select,
  Modal,
  Radio,
  AutoComplete,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  CopyOutlined,
  LockOutlined,
  UnlockOutlined,
  PlusOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { Breadcrumb } from 'antd';
import { v2AnalysisApi, api, publicSamplesApi } from '../../../api';
import { getUserProfile } from '../../../auth';
import RegisterPromptModal from '../../Demo/RegisterPromptModal';
import moment from 'moment';
import 'moment/locale/ja';

moment.locale('ja');

const { Title } = Typography;

const AnalysisList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageState, setPageState] = useState({ current: 1, pageSize: 20 });
  const [userProfile, setUserProfile] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerAction, setRegisterAction] = useState('');
  const [searchText, setSearchText] = useState('');
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const pollingInFlightRef = React.useRef(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // 公開状態変更モーダル
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [shareStatus, setShareStatus] = useState('private');
  const [sharePassword, setSharePassword] = useState('');
  
  // 削除確認用（2回クリックで削除）
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const deleteConfirmTimerRef = React.useRef(null);
  
  // サーバーサイドフィルタ
  const [statusFilter, setStatusFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [allTags, setAllTags] = useState([]);
  
  // タグ追加用
  const [tagInputVisible, setTagInputVisible] = useState({});
  const [tagInputValue, setTagInputValue] = useState('');

  useEffect(() => {
    const init = async () => {
      await loadUserProfile();
    };
    init();
  }, []);

  // ユーザープロファイルまたはゲスト確定後にデータ読み込み
  useEffect(() => {
    if (userProfile || isGuest) {
      loadAnalyses();
    }
  }, [userProfile, isGuest]);

  // フィルター変更時に再読み込み
  useEffect(() => {
    if (userProfile) {
      loadAnalyses();
    }
  }, [selectedUserId, statusFilter, tagFilter]);

  // 画面非表示時はポーリング停止、復帰時に再開
  useEffect(() => {
    const onVisibility = () => setPollingEnabled(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // 5秒ごとの軽量ステータス自動更新（未完了のみ・ステータス列のみ更新）
  useEffect(() => {
    if (isGuest) return;
    const timer = setInterval(async () => {
      if (!pollingEnabled || pollingInFlightRef.current) return;
      try {
        pollingInFlightRef.current = true;
        // 軽量ステータスのみ取得（未完了分析）
        const lite = await v2AnalysisApi.listStatusLite();
        if (!Array.isArray(lite)) return;

        setAnalyses(prev => {
          if (!Array.isArray(prev) || prev.length === 0) return prev;
          const byId = new Map(prev.map(item => [item.analysis_id, item]));
          lite.forEach(s => {
            const ex = byId.get(s.analysis_id);
            if (ex) {
              byId.set(s.analysis_id, {
                ...ex,
                status: s.status,
                progress: s.progress,
                // 差異抑制：AI要約・公開状態も未完了範囲では最新に追随
                ai_overview_exists:
                  typeof s.ai_overview_exists === 'boolean'
                    ? s.ai_overview_exists
                    : ex.ai_overview_exists,
                share_status: s.share_status ?? ex.share_status,
              });
            }
          });
          return Array.from(byId.values());
        });
      } catch (err) {
        // noop
      } finally {
        pollingInFlightRef.current = false;
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [pageState.current, pageState.pageSize, pollingEnabled]);

  const loadUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsGuest(true);
      return;
    }
    try {
      const profile = await getUserProfile();
      setUserProfile(profile);
      if (profile.role === 'admin' || profile.role === 'master') {
        const response = await api.get('/users/', {
          params: { page_size: 1000 },
        });
        setUsers(response.data?.users || []);
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setIsGuest(true);
    }
  };

  const loadAnalyses = async (
    page = pageState.current,
    pageSize = pageState.pageSize,
  ) => {
    setLoading(true);
    try {
      if (isGuest) {
        const res = await publicSamplesApi.getSampleAnalysisList();
        const list = res?.analyses || [];
        setAnalyses(list);
        setTotalCount(res?.total_count || list.length);
        if (res?.all_tags) setAllTags(res.all_tags);
        setLoading(false);
        return;
      }
      const skip = (page - 1) * pageSize;
      const params = {
        skip,
        limit: pageSize,
        q: searchText || undefined,
        status: statusFilter || undefined,
        tag: tagFilter || undefined,
      };
      if (
        (userProfile?.role === 'admin' || userProfile?.role === 'master') &&
        selectedUserId
      ) {
        params.user_id = selectedUserId;
      }
      const res = await v2AnalysisApi.listAnalyses(params);
      const list = Array.isArray(res) ? res : res?.analyses || [];
      setAnalyses(list);
      setTotalCount(res?.total_count || list.length);
      if (res?.all_tags) {
        setAllTags(res.all_tags);
      }
    } catch (error) {
      console.error('Failed to load analyses:', error);
      if (!isGuest) {
        message.error('リサーチ結果の読み込みに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // タグ追加
  const handleAddTag = async (analysisId, newTag) => {
    if (!newTag.trim()) return;
    try {
      await v2AnalysisApi.addTag(analysisId, newTag.trim());
      message.success('タグを追加しました');
      loadAnalyses();
    } catch (e) {
      console.error('Failed to add tag:', e);
      message.error('タグの追加に失敗しました');
    }
    setTagInputVisible({});
    setTagInputValue('');
  };
  
  // タグ削除
  const handleRemoveTag = async (analysisId, tagToRemove, e) => {
    e.stopPropagation();
    try {
      await v2AnalysisApi.removeTag(analysisId, tagToRemove);
      message.success('タグを削除しました');
      loadAnalyses();
    } catch (e) {
      console.error('Failed to remove tag:', e);
      message.error('タグの削除に失敗しました');
    }
  };

  const handleDeleteClick = (analysisId, e) => {
    e.stopPropagation();
    
    if (deleteConfirmId === analysisId) {
      // 2回目のクリック → 削除実行
      if (deleteConfirmTimerRef.current) {
        clearTimeout(deleteConfirmTimerRef.current);
        deleteConfirmTimerRef.current = null;
      }
      setDeleteConfirmId(null);
      
      const doDelete = async () => {
        try {
          await v2AnalysisApi.deleteAnalysis(analysisId);
          message.success('リサーチ結果を削除しました');
          loadAnalyses();
        } catch (error) {
          console.error('Failed to delete analysis:', error);
          message.error('削除に失敗しました');
        }
      };
      doDelete();
    } else {
      // 1回目のクリック → 確認状態に
      setDeleteConfirmId(analysisId);
      
      // 3秒後に確認状態をリセット
      if (deleteConfirmTimerRef.current) {
        clearTimeout(deleteConfirmTimerRef.current);
      }
      deleteConfirmTimerRef.current = setTimeout(() => {
        setDeleteConfirmId(null);
      }, 3000);
    }
  };

  const getStatusTag = (status, progress) => {
    const statusConfig = {
      'started': { color: 'blue', icon: <ClockCircleOutlined />, text: '開始' },
      'running': {
        color: 'processing',
        icon: <SyncOutlined spin />,
        text: '分析中',
      },
      'completed': {
        color: 'success',
        icon: <CheckCircleOutlined />,
        text: '完了',
      },
      'error': {
        color: 'error',
        icon: <CloseCircleOutlined />,
        text: 'エラー',
      },
    };

    const config = statusConfig[status] || statusConfig['started'];

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
        {status === 'running' && typeof progress === 'number'
          ? ` ${progress}%`
          : ''}
      </Tag>
    );
  };

  const isAdmin =
    userProfile?.role === 'admin' || userProfile?.role === 'master';

  // 公開状態変更モーダルを開く
  const openShareModal = (record, e) => {
    if (e) e.stopPropagation();
    setSelectedAnalysis(record);
    setShareStatus(record.share_status || 'private');
    setSharePassword(record.share_password || '');
    setShareModalVisible(true);
  };

  // 公開状態を変更
  const handleUpdateShareStatus = async () => {
    if (!selectedAnalysis) return;

    try {
      await v2AnalysisApi.setShareStatus(selectedAnalysis.analysis_id, {
        status: shareStatus, // 'share_status'ではなく'status'
        password: shareStatus === 'password' ? sharePassword : null,
      });

      message.success('公開状態を変更しました');
      setShareModalVisible(false);
      loadAnalyses(); // リスト再読み込み
    } catch (error) {
      console.error('Failed to update share status:', error);

      // エラーメッセージを抽出
      let errorMessage = '公開状態の変更に失敗しました';

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // 配列の場合（バリデーションエラー）
        if (Array.isArray(detail)) {
          errorMessage = detail
            .map(err => err.msg || JSON.stringify(err))
            .join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    }
  };

  // 共有URLをコピー
  const copyShareUrl = () => {
    if (!selectedAnalysis) return;

    const shareUrl = `${window.location.origin}/shared/v2-analysis/${selectedAnalysis.analysis_id}`;
    navigator.clipboard.writeText(shareUrl);
    message.success('URLをコピーしました');
  };

  const columns = [
    {
      title: '',
      key: 'open_new_tab',
      width: 40,
      render: (_, record) => (
        <Tooltip title="別タブで開く">
          <span
            onClick={e => {
              e.stopPropagation();
              window.open(`/aio/analysis/${record.analysis_id}`, '_blank');
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: '#fff',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#1890ff';
              e.currentTarget.style.color = '#1890ff';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#d9d9d9';
              e.currentTarget.style.color = 'inherit';
            }}
          >
            <ExportOutlined style={{ fontSize: 12 }} />
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'キーワード',
      dataIndex: 'keyword',
      key: 'keyword',
      render: text => <strong>{text}</strong>,
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => getStatusTag(record.status, record.progress),
      width: 120,
    },
    {
      title: 'タグ',
      key: 'tags',
      width: 200,
      render: (_, record) => {
        const tags = record.tags || [];
        if (isGuest || record.is_sample) {
          return (
            <Space size={4} wrap>
              {tags.map(tag => (
                <Tag key={tag} style={{ marginRight: 4 }}>{tag}</Tag>
              ))}
            </Space>
          );
        }
        const isInputVisible = tagInputVisible[record.analysis_id];
        return (
          <Space size={4} wrap onClick={e => e.stopPropagation()}>
            {tags.map(tag => (
              <Tag
                key={tag}
                closable
                onClose={e => handleRemoveTag(record.analysis_id, tag, e)}
                style={{ marginRight: 4 }}
              >
                {tag}
              </Tag>
            ))}
            {isInputVisible ? (
              <AutoComplete
                size="small"
                style={{ width: 120 }}
                value={tagInputValue}
                onChange={v => setTagInputValue(v)}
                onBlur={() => {
                  handleAddTag(record.analysis_id, tagInputValue);
                }}
                onSelect={(v) => {
                  handleAddTag(record.analysis_id, v);
                }}
                options={allTags
                  .filter(t => 
                    !tags.includes(t) && 
                    t.toLowerCase().includes((tagInputValue || '').toLowerCase())
                  )
                  .map(t => ({ value: t, label: t }))}
                placeholder="タグを入力"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag(record.analysis_id, tagInputValue);
                  }
                }}
              />
            ) : (
              <Tag
                style={{ 
                  background: '#fff', 
                  borderStyle: 'dashed',
                  cursor: 'pointer',
                }}
                onClick={e => {
                  e.stopPropagation();
                  setTagInputVisible({ [record.analysis_id]: true });
                  setTagInputValue('');
                }}
              >
                <PlusOutlined />
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '公開状態',
      dataIndex: 'share_status',
      key: 'share_status',
      render: (v, record) => (
        <Space size="small">
          {v === 'public' ? (
            <Tag color="green">公開</Tag>
          ) : v === 'password' ? (
            <Tag color="gold">限定公開</Tag>
          ) : (
            <Tag>非公開</Tag>
          )}
          <Tooltip title="公開設定を変更">
            <EditOutlined
              onClick={e => openShareModal(record, e)}
              style={{ color: '#1890ff', cursor: 'pointer' }}
            />
          </Tooltip>
        </Space>
      ),
      width: 140,
    },
    {
      title: '作成日時',
      dataIndex: 'created_at',
      key: 'created_at',
      render: date => moment(date).format('YYYY/MM/DD HH:mm'),
      width: 160,
      sorter: (a, b) =>
        moment(a.created_at).unix() - moment(b.created_at).unix(),
    },
    ...(isAdmin
      ? [
          {
            title: '作成者',
            key: 'owner',
            width: 80,
            align: 'center',
            render: (_, record) => (
              <Tooltip
                title={
                  <span style={{ whiteSpace: 'pre-line' }}>
                    {[record.company_name, record.user_name, record.user_email]
                      .filter(Boolean)
                      .join('\n')}
                  </span>
                }
              >
                <span
                  onClick={e => e.stopPropagation()}
                  style={{
                    display: 'inline-flex',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#f0f0f0',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    color: '#595959',
                  }}
                >
                  {(record.user_name || record.user_email || 'U')
                    .toString()
                    .trim()
                    .slice(0, 1)
                    .toUpperCase()}
                </span>
              </Tooltip>
            ),
          },
        ]
      : []),
    // 削除カラム（全ユーザー向け、2回クリックで削除）
    {
      title: '',
      key: 'delete',
      width: 48,
      align: 'center',
      render: (_, record) => {
        if (record.is_sample) return null;
        const isConfirming = deleteConfirmId === record.analysis_id;
        return (
          <Tooltip title={isConfirming ? 'もう一度クリックで削除' : '削除'}>
            <DeleteOutlined
              onClick={e => handleDeleteClick(record.analysis_id, e)}
              style={{ 
                color: isConfirming ? '#ff4d4f' : '#999',
                cursor: 'pointer',
                fontSize: isConfirming ? 18 : 16,
                transition: 'all 0.2s',
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>
          <Link to="/aio/dashboard">AIOコンテンツモード</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>リサーチ結果一覧</Breadcrumb.Item>
      </Breadcrumb>
      {/* ヘッダー（スタイル管理準拠） */}
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={1} className="gradient-text" style={{ fontSize: 24 }}>
              リサーチ結果一覧
            </Title>
            <Typography.Text type="secondary">
              最近のリサーチ結果を管理・検索できます
            </Typography.Text>
          </Col>
        </Row>
      </div>

      {/* アクションバー（新規＋検索） */}
      <div
        className="analysis-list-actions"
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (isGuest) {
                setRegisterAction('新規分析');
                setShowRegisterModal(true);
              } else {
                navigate('/aio/analysis/new');
              }
            }}
            style={{ minWidth: 160, fontWeight: 'bold' }}
          >
            新規分析
          </Button>
          <Input
            placeholder="キーワードで検索..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => loadAnalyses(1, pageState.pageSize)}
            allowClear
            style={{ minWidth: 200 }}
          />
          <Select
            allowClear
            placeholder="ステータス"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v);
              setPageState(prev => ({ ...prev, current: 1 }));
            }}
            style={{ minWidth: 120 }}
            options={[
              { value: null, label: '全てのステータス' },
              { value: 'running', label: '分析中' },
              { value: 'completed', label: '完了' },
              { value: 'error', label: 'エラー' },
            ]}
          />
          <Select
            allowClear
            placeholder="タグ"
            value={tagFilter}
            onChange={v => {
              setTagFilter(v);
              setPageState(prev => ({ ...prev, current: 1 }));
            }}
            style={{ minWidth: 120 }}
            options={[
              { value: null, label: '全てのタグ' },
              ...allTags.map(tag => ({ value: tag, label: tag })),
            ]}
          />
          {isAdmin && (
            <Select
              allowClear
              showSearch
              placeholder="全てのユーザー"
              value={selectedUserId}
              onChange={setSelectedUserId}
              style={{ minWidth: 200 }}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={[
                { value: null, label: '全てのユーザー' },
                ...users.map(user => ({
                  value: user.user_id,
                  label: `${user.last_name}${user.first_name} (${user.email})${
                    user.company_name ? ` - ${user.company_name}` : ''
                  }`,
                })),
              ]}
            />
          )}
        </Space>
        <span />
      </div>

      <Table
        columns={columns}
        dataSource={analyses}
        rowKey="analysis_id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <Empty
              description="リサーチ結果がありません"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (isGuest) {
                    setRegisterAction('新規分析');
                    setShowRegisterModal(true);
                  } else {
                    navigate('/aio/analysis/new');
                  }
                }}
              >
                新規分析を開始
              </Button>
            </Empty>
          ),
        }}
        onRow={record => ({
          onClick: () => {
            navigate(`/aio/analysis/${record.analysis_id}`);
          },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: pageState.current,
          pageSize: pageState.pageSize,
          total: totalCount,
          showSizeChanger: true,
          showTotal: total => `全 ${total} 件`,
          onChange: (page, pageSize) => {
            setPageState({ current: page, pageSize });
            loadAnalyses(page, pageSize);
          },
        }}
      />

      {/* 公開状態変更モーダル */}
      <Modal
        title="公開設定"
        open={shareModalVisible}
        onOk={handleUpdateShareStatus}
        onCancel={() => setShareModalVisible(false)}
        okText="保存"
        cancelText="キャンセル"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Radio.Group
            value={shareStatus}
            onChange={e => setShareStatus(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio value="private">
                <Space>
                  <LockOutlined />
                  <span>非公開</span>
                </Space>
              </Radio>
              <Radio value="public">
                <Space>
                  <UnlockOutlined />
                  <span>公開</span>
                </Space>
              </Radio>
              <Radio value="password">
                <Space>
                  <LockOutlined />
                  <span>パスワード保護</span>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>

          {shareStatus === 'password' && (
            <div>
              <Typography.Text
                strong
                style={{ display: 'block', marginBottom: 8 }}
              >
                パスワード
              </Typography.Text>
              <Input.Password
                value={sharePassword}
                onChange={e => setSharePassword(e.target.value)}
                placeholder="パスワードを入力"
              />
            </div>
          )}

          {(shareStatus === 'public' || shareStatus === 'password') &&
            selectedAnalysis && (
              <div>
                <Typography.Text
                  strong
                  style={{ display: 'block', marginBottom: 8 }}
                >
                  共有URL
                </Typography.Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    value={`${window.location.origin}/shared/v2-analysis/${selectedAnalysis.analysis_id}`}
                    readOnly
                  />
                  <Tooltip title="URLをコピー">
                    <Button icon={<CopyOutlined />} onClick={copyShareUrl} />
                  </Tooltip>
                </Space.Compact>
              </div>
            )}
        </Space>
      </Modal>

      <RegisterPromptModal
        open={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        triggerAction={registerAction}
      />
    </>
  );
};

export default AnalysisList;
